import React, { FC, useEffect, useState, useContext, useMemo } from 'react';
import injectSheet from 'react-jss';
import { Table } from 'HOC/Table';
import CheckBox from 'Components/CheckBox';
import ExportAudienceList from 'Components/ExportAudienceList';
import { Loading } from 'Components/Loading';
import LongTextEllipsis from 'Components/LongTextEllipsis';
import Tooltip from 'HOC/Tooltip';
import { FormattedDate } from 'Utils/formattedDate';
import mapVisitorType from 'Utils/mapVisitorType';
import { ProductAnalyticsContext } from 'Context/ProductAnalytics';
import { useRouter } from 'Utils/router';
import { motion } from 'framer-motion';
import { COLUMNS, AUDIENCE_COLUMN_CONFIG } from '../constants';
import styleSheet from './styles';

interface Props {
  checkedItems?: any;
  classes?: any;
  pageCount?: number;
  audienceData: Audience;
  tableColumns: string[];
  allColumns: string[];
  defaultColumns: string[];
  sortingColumn?: string;
  sortingDirection?: string;
  loading?: any;
  onChecked?: (n: string) => void;
  onUnChecked?: (n: string) => void;
  sort?: any;
  // This will be used to determine if a checkbox has been previously checked while navigating through other pages
  isChecked?: (id: string) => boolean;
  columnConfig?: any;
  defaultCellWidth?: string;
  columns?: any;
  changeCols?: any;
  currentPage?: number | string;
  totalPageCount?: number | string;
}

interface Audience {
  list: object[];
}

const calculateTableWidth = (tableColumns: string[]) => {
  let totalWidth = 0;
  for (let i = 0; i < tableColumns.length; i += 1) {
    const column = tableColumns[i];
    if (AUDIENCE_COLUMN_CONFIG[column]) {
      const columnWidth = AUDIENCE_COLUMN_CONFIG[column].widthInPixels;
      totalWidth += columnWidth + 48; // Add 24px left margin and 24px right margin for every column as per the design spec
    }
  }
  // Remove left margin (24px) for first column, add 64px for the checkbox
  totalWidth = totalWidth - 24 + 64;
  // At a minimum, the table width should be 1074px as per the design spec
  totalWidth = totalWidth <= 1074 ? 1074 : totalWidth;
  return `${totalWidth}px`;
};

const renderHeaders = (
  tableColumns: string[],
  columnConfig: any,
  defaultCellWidth: string,
  sort: any
) =>
  tableColumns.map((key: string, index: number) => {
    let response = null;
    // Tables aren't templated, therefore custom padding needs to be implemented to meet design spec //
    if (columnConfig[key]) {
      const colConfigAttr = columnConfig[key];
      const isSortable = (colConfigAttr && colConfigAttr.isSortable) || null;
      const title = (colConfigAttr && colConfigAttr.title) || 'undefined';
      const width =
        (colConfigAttr && colConfigAttr.widthInPixels) || defaultCellWidth;
      const sortKey = (colConfigAttr && colConfigAttr.sortKey) || undefined;
      const top = (colConfigAttr && colConfigAttr.toolTipMoveTop) || 0;
      const left = (colConfigAttr && colConfigAttr.toolTipMoveLeft) || 0;
      const toolTipWidth =
        (colConfigAttr && colConfigAttr.toolTipWidth) || 'auto';
      const doSort = isSortable && sortKey && sort;

      if (doSort) {
        // Add the sortKey if sort capabilities are being added to a column
        sort.sortKey = sortKey; //eslint-disable-line
      }

      response = (
        <Tooltip
          position="top"
          key={key}
          text={colConfigAttr && colConfigAttr.toolTip}
          offsetTop={top}
          offsetLeft={left}
          width={toolTipWidth}
          delay={0.5}
          overrideEvents={{
            showToolTip: ['onMouseEnter', 'onFocus'],
            hideToolTip: ['onBlur', 'onMouseLeave'],
          }}
        >
          <Table.Cell
            caret={
              sort &&
              sort.activeColumn &&
              sort.activeColumn.name === key &&
              isSortable
            }
            minWidth={width}
            key={title}
            sort={doSort ? { ...sort } : null}
            marginLeft={index === 0 ? '0px' : '24px'}
            marginRight="24px"
          >
            {title}
          </Table.Cell>
        </Tooltip>
      );
    }

    return response;
  });

const renderCell = (
  data: any,
  cellKey: string,
  value: any,
  index: number,
  callBacks?: any,
  columnConfig?: any,
  columns?: any,
  defaultCellWidth?: string,
  classes?: any
) => {
  if (columnConfig[cellKey]) {
    let strValue;
    const column = columnConfig[cellKey];
    const cellWidth =
      column && column.widthInPixels ? column.widthInPixels : defaultCellWidth;

    switch (cellKey) {
      case columns.CHECKBOX:
        return (
          // Stop click and keydown events from reaching TableRow and opening Detail view.
          // eslint-disable-next-line
          <div
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            style={{ cursor: 'auto' }}
          >
            <Table.Cell
              key={cellKey}
              minWidth={cellWidth}
              paddingRight="40px"
              paddingLeft="24px"
              paddingTop="26px"
              paddingBottom="15px"
              position="relative"
            >
              <div className={classes.checkBoxHolder}>
                <CheckBox
                  onClick={callBacks.onCheckBoxClick}
                  id={value}
                  isChecked={
                    callBacks.isChecked ? callBacks.isChecked(value) : null
                  }
                />
              </div>
            </Table.Cell>
          </div>
        );
      case columns.RELATIVE_INTEREST_LEVEL:
        return (
          <Table.Cell
            key={cellKey}
            minWidth={cellWidth}
            marginLeft={index === 0 ? '0px' : '24px'}
            marginRight="24px"
          >
            <Table.InterestBars level={value} />
          </Table.Cell>
        );
      case columns.DATE_OF_BIRTH:
        return (
          <Table.Cell
            key={cellKey}
            minWidth={cellWidth}
            marginLeft={index === 0 ? '0px' : '24px'}
            marginRight="24px"
          >
            {value !== '0001-01-01T00:00:00Z' ? (
              FormattedDate(value)
            ) : (
              <span>-</span>
            )}
          </Table.Cell>
        );
      case columns.VISITOR_TYPE:
        return (
          <Table.Cell
            key={cellKey}
            minWidth={cellWidth}
            marginLeft={index === 0 ? '0px' : '24px'}
            marginRight="24px"
          >
            {mapVisitorType(value, data.highSchoolLevelToday)}
          </Table.Cell>
        );
      case columns.ENROLL:
      case columns.GENDER:
      case columns.LEVEL_OF_STUDY:
      case columns.HAS_SCHEDULED_VISIT:
      case columns.HAS_APPLIED:
        return (
          <Table.Cell
            key={cellKey}
            minWidth={cellWidth}
            marginLeft={index === 0 ? '0px' : '24px'}
            marginRight="24px"
          >
            <span className={classes.capitalize}>{String(value)}</span>
          </Table.Cell>
        );
      case columns.GUARDIAN_NAME:
      case columns.GUARDIAN_EMAIL:
      case columns.GUARDIAN_PHONE:
        return (
          <Table.Cell
            key={cellKey}
            minWidth={cellWidth}
            marginLeft={index === 0 ? '0px' : '24px'}
            marginRight="24px"
          >
            <span>{value === 'NULL' ? '-' : value}</span>
          </Table.Cell>
        );
      case columns.PERSONALIZATION_SUGGESTIONS:
      case columns.PERSONALIZATION_PREFERENCES: {
        const hasValue = value !== 'NULL';
        return hasValue ? (
          <Tooltip position="bottom" text={value} offsetTop={45} offsetLeft={0}>
            <Table.Cell
              key={cellKey}
              minWidth={cellWidth}
              marginLeft={index === 0 ? '0px' : '24px'}
              marginRight="24px"
            >
              <LongTextEllipsis className={classes.name} value={value} />
            </Table.Cell>
          </Tooltip>
        ) : (
          <Table.Cell
            key={cellKey}
            minWidth={cellWidth}
            marginLeft={index === 0 ? '0px' : '24px'}
            marginRight="24px"
          >
            <span>-</span>
          </Table.Cell>
        );
      }
      default:
        strValue = String(value); // Need this to handle values that are false.  Would not pass the if statement
        if (strValue && strValue.length <= 46) {
          return (
            <Table.Cell
              key={cellKey}
              minWidth={cellWidth}
              marginLeft={index === 0 ? '0px' : '24px'}
              marginRight="24px"
            >
              {strValue}
            </Table.Cell>
          );
        }
        return (
          <Table.Cell
            key={cellKey}
            minWidth={cellWidth}
            marginLeft={index === 0 ? '0px' : '24px'}
            marginRight="24px"
          >
            {value !== undefined && String(value).substring(0, 46)}
          </Table.Cell>
        );
    }
  } else {
    return null;
  }
};
const setIndeterminate = (
  audienceData: any,
  checkedItems: any,
  pageCount: number
) => {
  let count = 0;
  audienceData.list.forEach((item: any) => {
    if (checkedItems.includes(item.id)) count += 1;
  });
  return count > 0 && count < pageCount;
};
const renderRows = (
  list: any,
  goToLink: (location: string) => void,
  callBacks?: any,
  columnConfig?: any,
  columns?: any,
  defaultCellWidth?: string,
  classes?: any,
  ViewName?: String,
  tableColumns?: string[]
) =>
  list.map((data: any) => {
    return (
      <motion.div
        key={data.id}
        initial={{ zIndex: 0, boxShadow: '0 2px 8px 0 rgba(0,0,0,0)' }}
        transition={{ duration: 0.2 }}
        whileHover={{ zIndex: 10, boxShadow: '0 2px 8px 0 rgba(0,0,0,0.15)' }}
      >
        <Table.Row
          padding="12px 0px"
          justifyContent="flex-start"
          color="#051D39"
          onClick={() => {
            goToLink(
              `/inquiries/detail/${data.userKeyConsolidated}/view/${ViewName}`
            );
          }}
        >
          {renderCell(
            data,
            'checkbox',
            data.id,
            0,
            callBacks,
            columnConfig,
            columns,
            defaultCellWidth,
            classes
          )}

          {tableColumns.map((key: string, indexTwo: number) =>
            renderCell(
              data,
              key,
              data[key],
              indexTwo,
              undefined,
              columnConfig,
              columns,
              defaultCellWidth,
              classes
            )
          )}
        </Table.Row>
      </motion.div>
    );
  });

const AudienceList: FC<Props> = (props: Props) => {
  const [exportSettings, setExportSettings] = useState(false);
  const [checkAll, setCheckAll] = useState(false);
  const {
    helpers: { View },
    actions: { track },
  } = useContext(ProductAnalyticsContext);

  const { location, push } = useRouter();
  // Used for Combined Analytics
  const ViewName = View(location.pathname);

  const {
    checkedItems,
    classes,
    pageCount,
    audienceData,
    tableColumns,
    allColumns,
    defaultColumns,
    sortingColumn,
    sortingDirection,
    loading,
    onChecked,
    onUnChecked,
    sort,
    isChecked,
    columnConfig,
    defaultCellWidth,
    columns,
    changeCols,
    currentPage,
    totalPageCount,
  } = props;

  const handleCheckAll = (checked: boolean) => {
    // Combined Analytics event tracking
    track('audience_selected', {
      scope: checked ? 'All on Page' : 'Selected',
      screen: ViewName,
      current_page: currentPage,
      total_page_count: totalPageCount,
    });
    setCheckAll(checked);
    audienceData.list.forEach((item: any) => {
      if (checked) {
        if (!checkedItems.includes(item.id)) {
          onChecked(item.id);
        }
      } else {
        onUnChecked(item.id);
      }
    });
  };

  const onCheckBoxClickHandler = (e: {
    target: { value: string; id: string };
  }) => {
    const checked = e.target.value;
    // Combined Analytics event tracking
    track('audience_selected', {
      scope: checked ? 'Selected' : '',
      screen: ViewName,
      current_page: currentPage,
      total_page_count: totalPageCount,
    });

    if (checked && onChecked) onChecked(e.target.id);
    else if (onUnChecked) onUnChecked(e.target.id);
  };

  useEffect(() => {
    // Checks if all rows are checked //
    let counter = 0;
    audienceData.list.forEach((item: any) => {
      if (checkedItems.includes(item.id)) {
        counter += 1;
      }
    });
    setCheckAll(false);
    // 10 is the page count so we know if there are 10 matches, they are all selected //
    if (counter === pageCount) {
      setCheckAll(true);
    }
  }, [audienceData]);

  const handleKeyEvent = (e: KeyboardEvent) => {
    const key = e.key.toLowerCase();
    if (key === 'escape') {
      setExportSettings(false);
    }
  };

  const goToLink = (loc: string) => {
    push({ pathname: loc, state: { prevPath: location.pathname } });
  };

  useEffect(() => {
    document.addEventListener('keydown', (e: any) => handleKeyEvent(e));
    return () => {
      document.removeEventListener('keydown', (e: any) => handleKeyEvent(e));
    };
  }, []);

  /* In the Table Heder Row, Tables aren't templated, therefore custom padding needs to be implemented to meet design spec */
  if (audienceData && tableColumns) {
    const tableWidth = useMemo(
      () => calculateTableWidth(tableColumns),
      [tableColumns]
    );

    return (
      <>
        <div
          className={classes.container}
          style={{ opacity: loading ? 0.4 : 1, marginTop: '25px' }}
        >
          <ExportAudienceList
            allColumns={allColumns}
            tableColumns={tableColumns}
            sortingColumn={sortingColumn}
            sortingDirection={sortingDirection}
            defaultColumns={defaultColumns}
            columnConfig={AUDIENCE_COLUMN_CONFIG}
            changeCols={changeCols}
            exportSettings={exportSettings}
            setExportSettings={(val: any) => setExportSettings(val)}
          />
          <Table
            overflowx="auto"
            overflowy="hidden"
            paddingTop="200px"
            marginTop="-200px"
          >
            <div
              style={{
                width: tableWidth,
              }}
            >
              <Table.Header>
                <Table.Row
                  padding="22px 0px 22px 0px"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                >
                  <Table.Cell
                    key="check"
                    minWidth="16px"
                    paddingRight="40px"
                    paddingLeft="24px"
                    paddingBottom="18px"
                    position="relative"
                    visibility="visible"
                  >
                    <div className={classes.checkBoxHolder}>
                      <CheckBox
                        isChecked={
                          setIndeterminate(
                            audienceData,
                            checkedItems,
                            pageCount
                          )
                            ? undefined
                            : checkAll
                        }
                        onClick={() => handleCheckAll(!checkAll)}
                      />
                    </div>
                  </Table.Cell>

                  {renderHeaders(
                    tableColumns,
                    columnConfig,
                    defaultCellWidth,
                    sort
                  )}
                </Table.Row>
              </Table.Header>
            </div>

            <div
              className={classes.bodyBackground}
              style={{
                width: tableWidth,
              }}
            >
              {audienceData.list && audienceData.list.length > 0 ? (
                <Table.Body>
                  {renderRows(
                    audienceData.list,
                    goToLink,
                    {
                      onCheckBoxClick: onCheckBoxClickHandler,
                      isChecked: isChecked || null,
                    },
                    columnConfig,
                    columns,
                    defaultCellWidth,
                    classes,
                    ViewName,
                    tableColumns
                  )}
                </Table.Body>
              ) : (
                <div className={classes.noResults}>
                  <span>[No Results]</span>
                </div>
              )}
            </div>
          </Table>
        </div>
        {loading && (
          <div className={classes.loaderHolder}>
            <Loading />
          </div>
        )}
      </>
    );
  }
  return (
    <div className={classes.loaderHolder}>
      <Loading />
    </div>
  );
};

AudienceList.defaultProps = {
  checkedItems: {},
  classes: {},
  pageCount: 0,
  sortingColumn: '',
  sortingDirection: '',
  loading: false,
  onChecked: () => {},
  onUnChecked: () => {},
  sort: {},
  isChecked: () => {
    return false;
  },
  columnConfig: AUDIENCE_COLUMN_CONFIG,
  defaultCellWidth: '',
  columns: COLUMNS,
  changeCols: {},
  currentPage: '',
  totalPageCount: '',
};

export default injectSheet(styleSheet)(AudienceList);
